import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const scoreColor = (s) => (s >= 70 ? "#16a34a" : s >= 50 ? "#f59e0b" : "#ef4444");

function IssueBlock({ title, icon, data }) {
  const critical = data?.critical || [];
  const generic = data?.generic || [];
  if (critical.length === 0 && generic.length === 0) return null;
  return (
    <div className="rounded-2xl border border-border p-4">
      <p className="font-semibold flex items-center gap-2"><span>{icon}</span> {title}</p>
      {critical.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-red-600 mb-1">Critical</p>
          {critical.map((it, i) => (
            <div key={i} className="flex items-start text-sm text-red-900"><span className="text-red-500 mr-2">●</span><span className="flex-1">{it}</span></div>
          ))}
        </div>
      )}
      {generic.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">General</p>
          {generic.map((it, i) => (
            <div key={i} className="flex items-start text-sm text-slate-700"><span className="text-slate-400 mr-2">○</span><span className="flex-1">{it}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PatientSummaryDialog({ open, onOpenChange, loading, summary, patientName }) {
  const s = summary || {};
  const score = s.overallHealthScore?.score;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto rounded-3xl" data-testid="patient-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left"><Sparkles className="h-5 w-5 text-primary" /> AI Health Summary</DialogTitle>
          <DialogDescription className="text-left">
            {s.patientName || patientName || "Patient"}
            {s.recordCount ? ` • ${s.recordCount} record${s.recordCount === 1 ? "" : "s"} analyzed` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Generating AI-powered health summary…</p>
            <p className="text-xs text-muted-foreground">This may take a minute</p>
          </div>
        ) : !summary ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No summary available.</p>
        ) : (
          <div className="space-y-4">
            {typeof score === "number" && (
              <div className="flex items-center gap-4 rounded-2xl border border-border p-4">
                <div className="h-16 w-16 rounded-full border-4 flex items-center justify-center shrink-0" style={{ borderColor: scoreColor(score) }}>
                  <span className="text-xl font-extrabold" style={{ color: scoreColor(score) }}>{score}</span>
                </div>
                <p className="text-sm text-slate-700 flex-1">{s.overallHealthScore?.interpretation}</p>
              </div>
            )}

            <IssueBlock title="Past Health Issues" icon="📜" data={s.pastIssues} />
            <IssueBlock title="Current Health Issues" icon="🏥" data={s.currentIssues} />

            {s.futureRisks?.length > 0 && (
              <div className="rounded-2xl border border-border p-4">
                <p className="font-semibold flex items-center gap-2">⚠️ Future Health Risks</p>
                <div className="mt-2 space-y-3">
                  {s.futureRisks.map((risk, i) => (
                    <div key={i} className="rounded-xl bg-muted/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{risk.risk}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${risk.probability === "High" ? "bg-red-100 text-red-700" : risk.probability === "Medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{risk.probability}</span>
                      </div>
                      {risk.basedOn && <p className="text-xs text-muted-foreground mt-1">Based on: {risk.basedOn}</p>}
                      {risk.preventionTips?.length > 0 && (
                        <div className="mt-1.5">
                          <p className="text-xs font-medium">Prevention</p>
                          {risk.preventionTips.map((tip, j) => (
                            <p key={j} className="text-xs text-slate-600">• {tip}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {s.vaccineRecommendations?.length > 0 && (
              <div className="rounded-2xl border border-border p-4">
                <p className="font-semibold flex items-center gap-2">💉 Vaccine Recommendations</p>
                <div className="mt-2 space-y-2">
                  {s.vaccineRecommendations.map((v, i) => (
                    <div key={i} className="rounded-xl bg-muted/50 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">{v.vaccine}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${v.priority === "High" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{v.priority}</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{v.reason}</p>
                      {v.source && <p className="text-[11px] text-muted-foreground mt-0.5">Source: {v.source}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {s.aiInsights && (
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <p className="font-semibold flex items-center gap-2">🤖 AI Health Insights</p>
                <p className="text-sm text-slate-700 mt-2 leading-6 whitespace-pre-line">{s.aiInsights}</p>
              </div>
            )}

            {s.missingData?.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="font-semibold text-amber-800 text-sm">📝 Missing Information</p>
                {s.missingData.map((it, i) => (
                  <p key={i} className="text-xs text-amber-800 mt-1">• {it}</p>
                ))}
              </div>
            )}

            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Informational only{s.aiPowered ? " • AI-Powered" : ""}. Not a substitute for professional medical advice.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
